import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";

import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import Box from "ds/components/Box";
import Banner from "ds/components/Banner";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import useMigrateStackVendor from "shared/Stack/useMigrateStackVendor";
import { StackVendor } from "types/generated";

import { REFETCH_STACK_AND_STACK_SETTINGS_QUERIES } from "../../../constants";

type StackSettingsTerragruntRollbackConfirmationModalProps = {
  stackId: string;
};

const StackSettingsTerragruntRollbackConfirmationModal = create(
  function StackSettingsTerragruntRollbackConfirmationModal({
    stackId,
  }: StackSettingsTerragruntRollbackConfirmationModalProps) {
    const modal = useModal();
    const { reportSuccess } = useTypedContext(FlashContext);

    const [migrateVendor, { loading }] = useMigrateStackVendor({
      refetchQueries: REFETCH_STACK_AND_STACK_SETTINGS_QUERIES,
    });

    const handleConfirm = useCallback(() => {
      migrateVendor(
        {
          id: stackId,
          targetVendor: StackVendor.Terraform,
          config: { labels: ["terragrunt"], terragrunt: null },
        },
        (data) => {
          if (data?.stackMigrateVendor) {
            reportSuccess({ message: "Stack vendor was successfully rolled back" });
            modal.hide();
          }
        }
      );
    }, [migrateVendor, modal, reportSuccess, stackId]);

    return (
      <ConfirmationModal
        title="Confirm Rollback"
        confirmationButtonLabel="Rollback"
        confirmationButtonVariant="dangerPrimary"
        onConfirm={handleConfirm}
        isLoading={loading}
        isDismissable={!loading}
      >
        <Box direction="column" gap="medium">
          <ConfirmationModalMessage>
            This will convert your Terragrunt stack back to a Terraform stack with Terragrunt
            labels.
          </ConfirmationModalMessage>
          <Banner variant="warning">
            Terragrunt via labels is deprecated and will be removed in the future.
          </Banner>
        </Box>
      </ConfirmationModal>
    );
  }
);

export const showStackSettingsTerragruntRollbackConfirmationModal = (
  props: StackSettingsTerragruntRollbackConfirmationModalProps
) => show(StackSettingsTerragruntRollbackConfirmationModal, props);
